/**
 * Heritage-ämnessidor (sökordskluster under /heritage/).
 *
 * Varje topic blir en egen landningssida via heritageTopicRoute().
 * Prisspann speglar den centrala prisdatan i priceGuides.ts
 * (livsberattelse-film och kunskapsoverforing) — inga nya siffror hittas på.
 */

export interface HeritageTopicTier {
  label: string;
  range: string;
  scope: string;
}

export interface HeritageTopic {
  /** URL-segment efter /heritage/ */
  slug: string;
  audience: "privat" | "foretag";
  eyebrow: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  /** 1–2 meningar direkt under H1. */
  lede: string;
  /** Citerbart definitionssvar (QuickAnswer + QAPage). */
  quickAnswer: string;
  /** Frågan som quickAnswer besvarar. */
  question: string;
  intro: string[];
  useCasesTitle: string;
  useCases: string[];
  process: { label: string; body: string }[];
  tiers: HeritageTopicTier[];
  /** Punkter som styr pris/omfattning eller minskar risk. */
  notes: { title: string; items: string[] };
  faq: { q: string; a: string }[];
  serviceName: string;
  serviceType: string;
  breadcrumbLabel: string;
  relatedPricePath: "/pris/livsberattelse-film" | "/pris/kunskapsoverforing" | "/pris/minnesfilm";
  relatedPriceLabel: string;
  /** Engelsk spegling under /en/heritage/<slug> (hreflang-par). */
  en?: HeritageTopicEn;
}

/** Engelskt innehåll för ett topic. Samma struktur, översatt copy. */
export type HeritageTopicEn = Omit<
  HeritageTopic,
  "slug" | "audience" | "relatedPricePath" | "en"
>;

export const HERITAGE_TOPICS: HeritageTopic[] = [
  // ---------------------------------------------------------------- PRIVAT
  {
    slug: "livsberattelse-till-foralder",
    audience: "privat",
    eyebrow: "Heritage · För familjer",
    h1: "Livsberättelse till förälder eller mor- och farförälder",
    metaTitle: "Livsberättelse till förälder — filmad intervju | Fenny",
    metaDescription:
      "Ge en förälder eller mor- och farförälder en filmad livsberättelse. Vi planerar frågorna, spelar in tryggt hemma och levererar filmen privat. Från 35 000 kr.",
    lede:
      "Många vill dokumentera en förälders berättelse men vet inte hur man börjar. Vi tar hand om frågor, inspelning och klippning — ni behöver bara boka tiden.",
    question: "Hur dokumenterar man en förälders livsberättelse?",
    quickAnswer:
      "En förälders livsberättelse dokumenteras bäst genom en planerad, filmad intervju hemma hos personen: frågeområden förbereds tillsammans med familjen, intervjun spelas in i lugnt tempo och materialet klipps till en film som familjen äger privat.",
    intro: [
      "Det vanligaste uppdraget i Fenny Heritage är barn eller barnbarn som vill bevara en förälders berättelse innan tiden rinner ifrån. Initiativet kommer nästan alltid från familjen, inte från personen själv — och det är ofta där tveksamheten sitter: hur frågar man, vad ska man fråga om, och hur gör man det utan att det känns obekvämt?",
      "Vår roll är att ta bort friktionen. Vi håller ett förmöte med er i familjen, tar fram frågeområden tillsammans, och låter personen som ska intervjuas se frågorna i förväg om det skapar trygghet. Under inspelningen är vi få personer i rummet, tekniken är diskret och tempot är personens eget.",
      "Resultatet är en film som barnbarn och kommande generationer kan se — med röst, ansiktsuttryck, pauser och skratt bevarade, inte bara innehållet.",
    ],
    useCasesTitle: "Vanliga anledningar att göra det nu",
    useCases: [
      "Present till en förälder på högtidsdag eller jul",
      "Innan minnet eller hälsan förändras",
      "Barnbarn som ska få höra berättelsen i egna ord",
      "Efter ett dödsfall i familjen — den andra föräldern berättar",
      "Släkthistoria som bara en person fortfarande bär på",
      "Flytt från barndomshemmet innan platsen försvinner",
    ],
    process: [
      { label: "Förmöte med familjen", body: "Vi går igenom vem som ska intervjuas, syftet och vad ni vill ha bevarat." },
      { label: "Frågeunderlag", body: "Vi tar fram områden och frågor tillsammans. Personen får se dem i förväg om ni vill." },
      { label: "Filmad intervju", body: "Inspelning hemma eller på en plats med betydelse. Ett litet team, lugnt tempo." },
      { label: "Klippning", body: "Huvudfilm i kapitel, med möjlighet att väva in foton och arkivmaterial." },
      { label: "Privat leverans", body: "Filmen levereras digitalt till familjen. Ni bestämmer vem som får se den." },
    ],
    tiers: [
      { label: "Livsintervjun", range: "35–55 kkr", scope: "En person, förmöte, en filmad intervju och en klippt film." },
      { label: "Familjearvet", range: "65–95 kkr", scope: "Längre intervju, kapitelstruktur och inflätade foton." },
      { label: "Arvspaketet", range: "100–150 kkr+", scope: "Flera intervjuer, huvudfilm och kortare klipp." },
      { label: "Vid akut hälsosituation", range: "offert", scope: "Vi prioriterar och kan börja spela in inom någon vecka." },
    ],
    notes: {
      title: "Bra att veta innan ni frågar",
      items: [
        "Fråga tidigt. Nästan alla säger ja när de förstår att filmen är till familjen och inte publiceras.",
        "Personen behöver inte förbereda något själv — det är vårt jobb.",
        "En intervjudag är oftast 2–4 timmar med pauser, inte en heldag.",
        "Har ni gamla foton eller 8 mm-film: samla ihop dem, de kan vävas in.",
        "Materialet är ert. Vi använder det inte i marknadsföring utan skriftligt godkännande.",
      ],
    },
    faq: [
      {
        q: "Hur frågar jag min förälder om det här?",
        a: "Berätta att det är till barnbarnen och att filmen stannar i familjen. Det är den formuleringen som fungerar bäst — det handlar inte om att personen ska \"ställa upp\", utan om att berättelsen ska finnas kvar.",
      },
      {
        q: "Vad kostar en filmad livsberättelse?",
        a: "Typiskt 35 000–150 000 kr beroende på omfattning: en enskild intervju i den lägre delen, ett större familjearv med flera intervjuer och arkivmaterial högre upp. Offert lämnas efter första samtalet.",
      },
      {
        q: "Hur lång tid tar det från beslut till färdig film?",
        a: "Normalt 4–8 veckor. Vid sjukdom eller hög ålder prioriterar vi inspelningen och kan oftast filma inom någon vecka, och klippa efteråt.",
      },
      {
        q: "Kan flera i familjen intervjuas?",
        a: "Ja. Syskon, make eller maka och barn kan intervjuas samma dag om ni finns på samma plats — det håller nere kostnaden och ger berättelsen fler perspektiv.",
      },
      {
        q: "Vad händer om personen blir trött under inspelningen?",
        a: "Vi pausar. Intervjun följer personens tempo, och vi kan dela upp den på flera tillfällen om det behövs.",
      },
      {
        q: "Blir filmen offentlig?",
        a: "Nej. Filmen levereras digitalt direkt till er och delas inte vidare av oss.",
      },
    ],
    serviceName: "Filmad livsberättelse för förälder",
    serviceType: "Filmad livsberättelse",
    breadcrumbLabel: "Livsberättelse till förälder",
    relatedPricePath: "/pris/livsberattelse-film",
    relatedPriceLabel: "Se prisguide för livsberättelse på film",
  },
  {
    slug: "slakthistoria-pa-film",
    audience: "privat",
    eyebrow: "Heritage · Släkt och arkiv",
    h1: "Släkthistoria på film",
    metaTitle: "Släkthistoria på film — bevara släktens historia | Fenny",
    metaDescription:
      "Förvandla släktforskning, gamla foton och 8 mm-film till en film som familjen faktiskt tittar på. Filmade intervjuer plus arkivmaterial. Från 35 000 kr.",
    
    lede:
      "Släktforskningen finns i pärmar och filer. Filmen är det som barnen och barnbarnen faktiskt tar del av — berättad av dem som var med.",
    question: "Hur gör man film av sin släkthistoria?",
    quickAnswer:
      "Släkthistoria blir film genom att kombinera filmade intervjuer med de äldsta i släkten och digitaliserat arkivmaterial — foton, brev, dokument och äldre film — i en berättelse med tydlig kronologi eller tema.",
    intro: [
      "Släktforskning ger namn, årtal och platser. Det som saknas är rösten: varför flytten skedde, hur det kändes, vad som aldrig sades högt. Den delen finns bara hos de personer som fortfarande minns, och den försvinner snabbare än dokumenten.",
      "Vi bygger släkthistoriefilmer i två lager. Först intervjuar vi de personer som bär berättelsen. Sedan vävs ert arkivmaterial in — fotografier, brev, kartor, gamla hemmafilmer, tidningsurklipp — så att det som finns i pärmarna får sammanhang.",
      "Resultatet kan struktureras kronologiskt (generation för generation) eller tematiskt (gården, emigrationen, kriget, företaget). Vi rekommenderar upplägg utifrån vilket material ni har.",
    ],
    useCasesTitle: "Passar när ni har",
    useCases: [
      "Släktforskning som ingen i familjen orkar läsa",
      "Lådor med foton utan sammanhang",
      "8 mm- eller VHS-material som behöver digitaliseras",
      "En gård, ett hus eller ett familjeföretag med historia",
      "Emigrations- eller invandringshistoria i släkten",
      "Släktträff eller jubileum där filmen kan visas",
    ],
    process: [
      { label: "Materialgenomgång", body: "Vi går igenom vad ni har: forskning, foton, film, dokument och vem som kan berätta." },
      { label: "Berättarstruktur", body: "Vi föreslår kronologi eller teman och vilka intervjuer som bär berättelsen." },
      { label: "Intervjuer", body: "Filmade intervjuer med de personer som minns — ofta flera generationer." },
      { label: "Arkivbearbetning", body: "Digitalisering, färgkorrigering och rörelse på stillbilder där det höjer berättelsen." },
      { label: "Klippning och leverans", body: "Huvudfilm plus kortare kapitel per gren eller tema, levererat digitalt." },
    ],
    tiers: [
      { label: "Livsintervjun", range: "35–55 kkr", scope: "En berättare, en intervju, klippt film utan större arkivarbete." },
      { label: "Familjearvet", range: "65–95 kkr", scope: "Intervju plus inflätat foto- och arkivmaterial, kapitelstruktur." },
      { label: "Arvspaketet", range: "100–150 kkr+", scope: "Flera generationer, omfattande arkiv, huvudfilm och korta klipp." },
    ],
    notes: {
      title: "Vad som styr omfattningen",
      items: [
        "Mängden arkivmaterial som ska digitaliseras och bearbetas.",
        "Antal personer som ska intervjuas och om de bor på olika orter.",
        "Om berättelsen ska täcka en gren eller hela släkten.",
        "Om ni vill ha inspelning på platser som gården, hembygden eller kyrkan.",
        "Om filmen ska visas på släktträff — då gör vi ofta en kortversion också.",
      ],
    },
    faq: [
      {
        q: "Vi har redan gjort släktforskningen. Kan ni utgå från den?",
        a: "Ja, det är den bästa utgångspunkten. Forskningen ger struktur och fakta; vi lägger till intervjuerna och arkivmaterialet som gör den berättad.",
      },
      {
        q: "Kan ni digitalisera gamla filmer och foton?",
        a: "Ja. Vi hanterar digitalisering och bildbearbetning som en del av produktionen när materialet ska in i filmen.",
      },
      {
        q: "Hur lång blir en släkthistoriefilm?",
        a: "Huvudfilmen är oftast 20–45 minuter, med kortare kapitel som är lättare att titta på i vardagen eller visa på en släktträff.",
      },
      {
        q: "Kan vi filma på plats där släkten kommer ifrån?",
        a: "Ja. Inspelning på gården, i hembygden eller vid huset ger filmen förankring. Resor utanför våra normala områden offereras separat.",
      },
      {
        q: "Vad kostar det?",
        a: "Typiskt 35 000–150 000 kr. Priset styrs framför allt av antal intervjuer och hur mycket arkivmaterial som ska bearbetas.",
      },
      {
        q: "Vem äger materialet?",
        a: "Ni. Filmen och råmaterialet levereras till familjen och vi publicerar ingenting utan ert godkännande.",
      },
    ],
    serviceName: "Släkthistoria på film",
    serviceType: "Filmad släkthistoria",
    breadcrumbLabel: "Släkthistoria på film",
    relatedPricePath: "/pris/livsberattelse-film",
    relatedPriceLabel: "Se prisguide för livsberättelse på film",
  },
  {
    slug: "minnesfilm",
    audience: "privat",
    eyebrow: "Heritage · Tillfällen",
    h1: "Minnesfilm",
    metaTitle: "Minnesfilm — film till jubileum, högtidsdag och minnesstund",
    metaDescription:
      "Minnesfilm till 80-årsdagen, jubileet eller minnesstunden. Filmade hälsningar, intervjuer och gamla foton klippt till en film att visa tillsammans.",
    lede:
      "En film som visas när familjen ändå är samlad — på högtidsdagen, jubileet eller minnesstunden.",
    question: "Vad är en minnesfilm?",
    quickAnswer:
      "En minnesfilm är en kortare film som sammanfattar en människas liv eller en familjs gemensamma minnen, ofta byggd av filmade hälsningar, intervjuklipp och fotografier, och gjord för att visas vid en högtidsdag, ett jubileum eller en minnesstund.",
    intro: [
      "Minnesfilmen skiljer sig från den fullständiga livsberättelsen genom sitt syfte: den ska visas för en samlad grupp vid ett bestämt tillfälle. Därför är den kortare, mer komprimerad och byggd för att bära känsla i ett rum med människor.",
      "Vanliga byggstenar är korta filmade hälsningar från barn och barnbarn, klipp ur en intervju med huvudpersonen, fotografier från olika årtionden och musik. Vi kan spela in hälsningar på plats, på distans eller kombinera.",
      "Många familjer beställer en minnesfilm och en full livsberättelse i samma produktion — intervjumaterialet räcker till båda, och det blir betydligt billigare än två separata inspelningar.",
    ],
    useCasesTitle: "Passar till",
    useCases: [
      "80-, 90- eller 100-årsdag",
      "Guldbröllop och andra bröllopsjubileum",
      "Minnesstund och begravning",
      "Släktträff eller familjejubileum",
      "Avtackning efter ett långt yrkesliv",
      "Filmad hälsning från en hel familj",
    ],
    process: [
      { label: "Upplägg och tidsplan", body: "Vi utgår från datumet filmen ska visas och räknar bakåt." },
      { label: "Insamling", body: "Foton, gamla filmer och önskemål om musik samlas in från familjen." },
      { label: "Hälsningar och intervju", body: "Vi spelar in hälsningar och, om det passar, en intervju med huvudpersonen." },
      { label: "Klippning", body: "Filmen klipps till visningslängd med rätt tempo, musik och textning." },
      { label: "Leverans", body: "Digital leverans i format som fungerar på plats — projektor, tv eller skärm." },
    ],
    tiers: [
      { label: "Minnesfilm kort", range: "från 35 kkr", scope: "Foton, insamlat material och enklare klippning till visningsfilm." },
      { label: "Minnesfilm + intervju", range: "45–75 kkr", scope: "Filmad intervju med huvudpersonen plus minnesfilm ur samma material." },
      { label: "Familjearvet", range: "65–95 kkr", scope: "Full livsberättelse med minnesfilm som kortversion." },
    ],
    notes: {
      title: "Planera i tid",
      items: [
        "Boka minst 6 veckor före visningsdatumet när det går.",
        "Vid minnesstund arbetar vi med kortare varsel — hör av er direkt.",
        "Samla foton digitalt om möjligt; skannade original ger bäst kvalitet.",
        "Hälsningar kan spelas in på distans om familjen är utspridd.",
        "Bestäm visningslängd tidigt — 5–12 minuter fungerar bäst i ett rum.",
      ],
    },
    faq: [
      {
        q: "Hur lång ska en minnesfilm vara?",
        a: "5–12 minuter fungerar bäst när den visas för en samlad grupp. Längre filmer tappar rummet; kortare hinner inte bära känslan.",
      },
      {
        q: "Vad kostar en minnesfilm?",
        a: "Från cirka 35 000 kr för en film byggd på insamlat material. Med filmad intervju med huvudpersonen ligger den typiskt på 45 000–75 000 kr.",
      },
      {
        q: "Hur snabbt kan ni leverera inför en minnesstund?",
        a: "Vi hanterar korta varsel. Hör av er direkt så säger vi vad som är möjligt utifrån datum och material.",
      },
      {
        q: "Kan hälsningar spelas in på distans?",
        a: "Ja. Vi ger enkla instruktioner för mobilinspelning och ljudsätter och färgsätter materialet så att helheten håller ihop.",
      },
      {
        q: "Kan vi göra både minnesfilm och livsberättelse?",
        a: "Ja, och det är oftast det klokaste. En intervjuinspelning kan ge både en kort minnesfilm och en full livsberättelse.",
      },
      {
        q: "Vilka format levereras filmen i?",
        a: "Digitalt i format som fungerar på plats — projektor, tv eller skärm — plus en version för delning inom familjen.",
      },
    ],
    serviceName: "Minnesfilm",
    serviceType: "Minnesfilm",
    breadcrumbLabel: "Minnesfilm",
    relatedPricePath: "/pris/minnesfilm",
    relatedPriceLabel: "Se prisguide för minnesfilm",
  },
  {
    slug: "intervjufragor-livsberattelse",
    audience: "privat",
    eyebrow: "Heritage · Guide",
    h1: "Intervjufrågor för en livsberättelse",
    metaTitle: "60 intervjufrågor till en livsberättelse — frågebank | Fenny",
    metaDescription:
      "Frågebank för att intervjua en förälder eller mor- och farförälder om deras liv: uppväxt, arbete, kärlek, kriser och råd. Fri att använda.",
    lede:
      "En frågebank ni får använda fritt — samma struktur vi själva utgår från när vi planerar en filmad livsberättelse.",
    question: "Vilka frågor ska man ställa i en livsberättelseintervju?",
    quickAnswer:
      "Ställ öppna frågor som ber om konkreta situationer i stället för sammanfattningar: börja i barndomen, gå kronologiskt genom uppväxt, arbete, familj och vändpunkter, och avsluta med vad personen vill föra vidare. Fråga alltid \"berätta om en gång när…\" hellre än \"hur var det?\".",
    intro: [
      "Den vanligaste orsaken till att en livsberättelseintervju blir tunn är inte tekniken utan frågorna. Frågor som \"hur var din barndom?\" ger sammanfattningar. Frågor som \"berätta om köket i huset där du växte upp\" ger scener — och scener är vad som bär en film.",
      "Ordningen spelar roll. Börja i det konkreta och tidiga, där minnena är starkast och det känns ofarligt att prata. Svårare områden — förluster, kriser, konflikter — kommer först när samtalet har hittat sitt tempo, aldrig i början.",
      "Nedan är frågeområdena vi själva utgår från. Använd dem som de är om ni spelar in själva, eller som utgångspunkt för ett samtal med oss inför en produktion.",
    ],
    useCasesTitle: "Frågeområden",
    useCases: [
      "Barndom och plats: hemmet, rummen, dofterna, grannskapet",
      "Föräldrar och släkt: vilka de var, vad de gjorde, vad som sades och inte sades",
      "Skola och tidiga år: lärare, vänner, det första jobbet",
      "Arbete: vägen dit, stolthet, misstag, det som förändrades",
      "Kärlek och familj: mötet, besluten, vardagen",
      "Vändpunkter: flyttar, förluster, val som ändrade riktning",
      "Tid och samhälle: vad som var självklart då men inte nu",
      "Råd och arv: vad personen vill att barnbarnen ska veta",
    ],
    process: [
      { label: "Välj område, inte fråga", body: "Bestäm 5–8 områden i förväg. Följ samtalet inom området i stället för att beta av en lista." },
      { label: "Fråga efter scener", body: "\"Berätta om en gång när…\" ger konkreta minnen. \"Hur var det?\" ger sammanfattningar." },
      { label: "Var tyst efter svaret", body: "De bästa meningarna kommer i pausen efter att personen tror sig vara klar." },
      { label: "Ta det svåra i mitten", body: "Aldrig först, aldrig sist. Avsluta alltid i något varmt." },
      { label: "Avsluta med arvet", body: "\"Vad vill du att dina barnbarn ska veta om dig?\" är nästan alltid filmens sista mening." },
    ],
    tiers: [
      { label: "Gör det själv", range: "0 kr", scope: "Använd frågeområdena ovan, spela in med mobil på stativ och bra ljud." },
      { label: "Livsintervjun", range: "35–55 kkr", scope: "Vi planerar frågorna, intervjuar professionellt och klipper filmen." },
      { label: "Familjearvet", range: "65–95 kkr", scope: "Utvecklat frågeunderlag, längre intervju, kapitel och arkivmaterial." },
    ],
    notes: {
      title: "Om ni spelar in själva",
      items: [
        "Ljud är viktigare än bild. Använd en extern mikrofon nära personen.",
        "Sitt i ett rum med mjuka ytor — hårda rum ger eko som inte går att rädda.",
        "Placera personen med fönstret framför sig, inte bakom.",
        "Ställ kameran på stativ och låt den gå. Klipp inte under tiden.",
        "Spela in mer än ni tror behövs. Två timmar råmaterial blir kanske 30 minuter film.",
        "Säkerhetskopiera direkt, på två ställen.",
      ],
    },
    faq: [
      {
        q: "Hur många frågor behövs till en livsberättelseintervju?",
        a: "Räkna med 5–8 frågeområden snarare än en lista. Inom varje område räcker det oftast med 3–6 öppna frågor plus följdfrågor som ber om konkreta exempel.",
      },
      {
        q: "Ska personen få frågorna i förväg?",
        a: "Ofta ja. Det minskar oro och ger tid att minnas. Nackdelen är att svaren kan bli inövade — därför delar vi områden hellre än exakta formuleringar.",
      },
      {
        q: "Hur länge ska man intervjua?",
        a: "2–4 timmar med pauser är ett bra riktmärke för en person. Vid högre ålder delar vi hellre upp intervjun på flera tillfällen.",
      },
      {
        q: "Vad gör man när personen svarar kort?",
        a: "Byt från abstrakt till konkret. Fråga om en specifik dag, en plats eller ett föremål. Nästan alla korta svar öppnar sig när frågan blir mindre.",
      },
      {
        q: "Kan vi använda era frågor och sedan anlita er för klippning?",
        a: "Ja. Vi kan bearbeta och klippa material som familjen spelat in själva, förutsatt att ljudkvaliteten håller.",
      },
      {
        q: "Vad ska man undvika att fråga om?",
        a: "Inget område är förbjudet, men bestäm i förväg tillsammans med personen vad som får spelas in. Kommer något känsligt upp ändå går det alltid att klippa bort.",
      },
    ],
    serviceName: "Livsberättelseintervju",
    serviceType: "Filmad livsberättelse",
    breadcrumbLabel: "Intervjufrågor",
    relatedPricePath: "/pris/livsberattelse-film",
    relatedPriceLabel: "Se prisguide för livsberättelse på film",
  },

  // --------------------------------------------------------------- FÖRETAG
  {
    slug: "generationsskifte-dokumentation",
    audience: "foretag",
    eyebrow: "Heritage · För företag",
    h1: "Dokumentation vid generationsskifte",
    metaTitle: "Generationsskifte — dokumentera kunskapen på film | Fenny",
    metaDescription:
      "Vid generationsskifte försvinner kunskap som aldrig skrivits ner. Vi dokumenterar den på film: beslutslogik, kundrelationer och kultur. Från 60 000 kr.",
    lede:
      "Vid ett generationsskifte överlåts aktier och roller. Det som sällan överlåts är beslutslogiken, kundrelationerna och varför bolaget gör som det gör.",
    question: "Hur dokumenterar man kunskap vid ett generationsskifte?",
    quickAnswer:
      "Kunskap vid generationsskifte dokumenteras genom strukturerade filmade intervjuer med den avträdande generationen, planerade 6–12 månader före överlämningen och kapitlade per kunskapsområde så att den tillträdande kan gå tillbaka till rätt avsnitt när frågorna dyker upp.",
    intro: [
      "Ett generationsskifte hanteras oftast juridiskt och finansiellt med hög precision. Kunskapsöverföringen lämnas däremot åt tillfälliga samtal — och det som inte hinner sägas försvinner när den tidigare ägaren lämnar.",
      "Det handlar sällan om processer som redan finns dokumenterade. Det handlar om varför en viss kund alltid ringer direkt, varför ett leverantörsbyte gjordes 2009, vilka lösningar som testats och misslyckats, och vad som håller ihop kulturen.",
      "Vi lägger upp arbetet som en intervjuserie över tid, gärna med den tillträdande generationen i rummet. Materialet kapitlas per område och blir ett arbetsverktyg — inte ett arkiv som ingen öppnar.",
    ],
    useCasesTitle: "Typiska situationer",
    useCases: [
      "Ägarledda bolag där grundaren lämnar",
      "Familjeföretag som går till nästa generation",
      "Uppköp där nyckelpersonen slutar efter earn-out",
      "VD-byte i bolag med lång historia",
      "Styrelsebyten i stiftelser och föreningar",
      "Överlämning av kundansvar med långa relationer",
    ],
    process: [
      { label: "Kartläggning", body: "Vi identifierar vilka kunskapsområden som riskerar att försvinna och vem som bär dem." },
      { label: "Frågedesign", body: "Frågor som tvingar fram konkreta situationer och beslut, inte principer." },
      { label: "Intervjuserie", body: "Ett eller flera tillfällen, ofta med tillträdaren närvarande." },
      { label: "Kapitelklippning", body: "Materialet klipps per kunskapsområde med sökbar struktur." },
      { label: "Intern leverans", body: "Filerna levereras för intranät, LMS eller intern lagring. Sekretess enligt avtal." },
    ],
    tiers: [
      { label: "Nyckelperson-intervjun", range: "60–95 kkr", scope: "En person, förmöte, frågedesign, intervju och intern leverans." },
      { label: "Kunskapsöverföring", range: "100–180 kkr", scope: "Intervjuserie, kapitlat material för onboarding och utbildning." },
      { label: "Företagsarvet", range: "180–250 kkr+", scope: "Flera personer, sammanfattande film och korta internklipp." },
      { label: "Löpande dokumentation", range: "offert", scope: "Återkommande intervjuer planerade över året." },
    ],
    notes: {
      title: "Riskreducerare",
      items: [
        "Sekretessavtal tecknas innan första intervjun.",
        "Ni godkänner frågeunderlaget före inspelning.",
        "Materialet lagras enligt er policy och kan levereras för lokal lagring.",
        "Vi kan börja med en person och en kritisk roll innan ni bygger ut.",
        "Månadsvis, bindningsfritt om ni vill lägga upp det som löpande dokumentation.",
      ],
    },
    faq: [
      {
        q: "När i generationsskiftet bör dokumentationen göras?",
        a: "6–12 månader före överlämningen. Då finns tid för flera intervjutillfällen och för att den tillträdande hinner formulera sina egna frågor.",
      },
      {
        q: "Vad kostar det?",
        a: "Typiskt 60 000–250 000 kr beroende på antal nyckelpersoner och hur kapitlat materialet ska vara. Offert efter ett första samtal.",
      },
      {
        q: "Hur hanteras känslig information?",
        a: "Sekretessavtal tecknas före första intervjun, ni godkänner frågeunderlaget och materialet kan levereras för lagring helt i er egen miljö.",
      },
      {
        q: "Kan den tillträdande vara med i intervjun?",
        a: "Ja, och det rekommenderas ofta. Samtalet blir mer konkret när frågorna kommer från den som ska ta över.",
      },
      {
        q: "Vad skiljer det här från en processdokumentation?",
        a: "Processdokumentation beskriver hur arbetet ska göras. Kunskapsdokumentation på film fångar varför besluten ser ut som de gör — erfarenheten bakom processen.",
      },
      {
        q: "Hur används materialet efteråt?",
        a: "Vanligast är onboarding av tillträdaren, intern utbildning och som referens vid liknande beslut. Materialet kapitlas för att kunna sökas i, inte ses från början till slut.",
      },
    ],
    serviceName: "Dokumentation vid generationsskifte",
    serviceType: "Kunskapsöverföring på film",
    breadcrumbLabel: "Generationsskifte",
    relatedPricePath: "/pris/kunskapsoverforing",
    relatedPriceLabel: "Se prisguide för kunskapsöverföring",
  },
  {
    slug: "pensionsavgang-kunskapsoverforing",
    audience: "foretag",
    eyebrow: "Heritage · För företag",
    h1: "Kunskapsöverföring inför pensionsavgång",
    metaTitle: "Kunskapsöverföring inför pension — filmad intervju | Fenny",
    metaDescription:
      "Fånga erfarenheten innan seniora medarbetare går i pension. Strukturerade filmade intervjuer, kapitlade för onboarding. Från 60 000 kr.",
    lede:
      "En pensionsavgång är den enda kunskapsförlust ett företag känner till i förväg — och därmed den enda som går att förbereda sig på.",
    question: "Hur överför man kunskap innan någon går i pension?",
    quickAnswer:
      "Planera intervjuerna 6–12 månader före avgången, fokusera på det som inte finns dokumenterat — beslutslogik, kundhistorik, tidigare misslyckanden — och kapitla filmmaterialet per område så att efterträdaren kan gå tillbaka till rätt avsnitt vid behov.",
    intro: [
      "Sverige har en stor grupp medarbetare som lämnar arbetslivet de närmaste åren, och i många organisationer sitter de på kunskap som aldrig skrivits ner. Avskedsluncher ersätter inte överlämning.",
      "Det som är värt att fånga är sällan det som står i rutinbeskrivningen. Det är kundhistoriken, varför en viss lösning valdes, vilka fel som redan gjorts, och de bedömningar som en erfaren person gör utan att tänka på det.",
      "Vi arbetar med frågedesign som tvingar fram konkreta situationer — inte principer. Materialet kapitlas per kunskapsområde och blir användbart i onboarding av efterträdaren.",
    ],
    useCasesTitle: "Prioritera personer som",
    useCases: [
      "Är ensamma om en kritisk kompetens",
      "Har långa kundrelationer i eget förtroende",
      "Har varit med och byggt en process eller produkt",
      "Kan systemens historik och varför de ser ut som de gör",
      "Bär branschkunskap som inte finns i utbildningar",
      "Går i pension inom 12 månader",
    ],
    process: [
      { label: "Riskbedömning", body: "Vilka roller lämnar, och vilken kunskap saknar dokumentation?" },
      { label: "Frågedesign", body: "Vi bygger frågor kring situationer, beslut och undantag." },
      { label: "Intervju", body: "Ett eller flera tillfällen, ofta med efterträdaren närvarande." },
      { label: "Kapitelklippning", body: "Material per kunskapsområde, sökbart och användbart i utbildning." },
      { label: "Leverans", body: "Filer till intranät eller LMS, med sekretesshantering enligt avtal." },
    ],
    tiers: [
      { label: "Nyckelperson-intervjun", range: "60–95 kkr", scope: "En senior medarbetare, förmöte, frågedesign, intervju och leverans." },
      { label: "Kunskapsöverföring", range: "100–180 kkr", scope: "Intervjuserie, kapitlat material för onboarding och utbildning." },
      { label: "Företagsarvet", range: "180–250 kkr+", scope: "Flera personer, sammanfattande film och korta internklipp." },
      { label: "Löpande dokumentation", range: "offert", scope: "Planerade intervjuer kring årets pensionsavgångar." },
    ],
    notes: {
      title: "Så gör ni det kostnadseffektivt",
      items: [
        "Börja med en person och en kritisk roll — bygg ut när värdet är bevisat.",
        "Samla flera avgångar under samma inspelningsperiod.",
        "Låt efterträdaren ställa frågorna — det ger skarpare svar.",
        "Klipp huvudfilm och korta kapitelklipp ur samma inspelning.",
        "Planera i budget året innan avgången, inte månaden före.",
      ],
    },
    faq: [
      {
        q: "Hur långt före pensionen bör vi göra intervjun?",
        a: "6–12 månader före. Då finns utrymme för flera tillfällen och för efterträdaren att hinna formulera sina frågor.",
      },
      {
        q: "Vad kostar kunskapsöverföring på film?",
        a: "Typiskt 60 000–250 000 kr beroende på antal personer och hur kapitlat materialet ska vara.",
      },
      {
        q: "Vill medarbetare verkligen bli filmade?",
        a: "Nästan alla säger ja när syftet är tydligt: att deras erfarenhet ska tas till vara. Vi gör alltid ett förmöte där personen får veta exakt vad som ska hända.",
      },
      {
        q: "Hur långa blir intervjuerna?",
        a: "2–4 timmar per person, ofta uppdelat på flera tillfällen om kunskapsområdet är brett.",
      },
      {
        q: "Hur används materialet i onboarding?",
        a: "Genom kapitelklipp per område som nya medarbetare får se när det blir relevant, hellre än en lång film vid introduktionen.",
      },
      {
        q: "Kan materialet lagras i vår egen miljö?",
        a: "Ja. Vi levererar filerna för lagring enligt er policy, och sekretessavtal tecknas innan arbetet börjar.",
      },
    ],
    serviceName: "Kunskapsöverföring inför pension",
    serviceType: "Kunskapsöverföring på film",
    breadcrumbLabel: "Pensionsavgång",
    relatedPricePath: "/pris/kunskapsoverforing",
    relatedPriceLabel: "Se prisguide för kunskapsöverföring",
  },
  {
    slug: "foretagshistoria-pa-film",
    audience: "foretag",
    eyebrow: "Heritage · För företag",
    h1: "Företagshistoria på film",
    metaTitle: "Företagshistoria på film — jubileumsfilm | Fenny",
    metaDescription:
      "Dokumentera företagets historia på film inför jubileum, ägarskifte eller kulturarbete. Intervjuer med grundare och nyckelpersoner plus arkivmaterial.",
    lede:
      "En företagshistoria är människor och beslut, inte en logotyptidslinje. Vi hämtar den direkt från dem som var med.",
    question: "Hur dokumenterar man ett företags historia på film?",
    quickAnswer:
      "Företagshistoria dokumenteras genom filmade intervjuer med grundare, tidiga medarbetare och nyckelpersoner, kombinerat med arkivmaterial som foton, gamla filmer och dokument, strukturerat kronologiskt eller tematiskt kring avgörande skiften.",
    intro: [
      "Jubileum, ägarskifte, börsnotering eller ett kulturarbete som tappat fäste — anledningarna att dokumentera företagets historia varierar, men problemet är detsamma: de som var med finns inte kvar för alltid.",
      "Vi bygger materialet kring intervjuer med grundare, tidiga medarbetare, ägare och långa kundrelationer, och väver in arkivmaterial där det finns. Berättelsen struktureras antingen kronologiskt eller kring de skiften som formade bolaget.",
      "Leveransen är oftast en huvudfilm plus kortare klipp som fungerar på intranät, i rekrytering, på jubileet och i sociala kanaler — samma inspelning, flera användningsområden.",
    ],
    useCasesTitle: "Vanliga anledningar",
    useCases: [
      "Jubileum: 25, 50 eller 100 år",
      "Grundarporträtt innan grundaren lämnar",
      "Ägarskifte eller uppköp",
      "Kulturarbete och värderingsarbete internt",
      "Rekrytering och employer branding med verklig historia",
      "Kundberättelser från långa relationer",
    ],
    process: [
      { label: "Research", body: "Vi kartlägger tidslinjen, nyckelpersonerna och vilket arkiv som finns." },
      { label: "Berättarstruktur", body: "Kronologi eller teman — vi föreslår det upplägg materialet bär." },
      { label: "Intervjuer", body: "Grundare, tidiga medarbetare, ägare och kunder." },
      { label: "Arkiv och klippning", body: "Foton, gamla filmer och dokument bearbetas in i berättelsen." },
      { label: "Leverans", body: "Huvudfilm plus kortare klipp för intranät, jubileum och kanaler." },
    ],
    tiers: [
      { label: "Grundarporträttet", range: "60–95 kkr", scope: "En intervju, klippt film, enklare arkivbearbetning." },
      { label: "Företagshistoria", range: "100–180 kkr", scope: "Flera intervjuer, arkivmaterial, huvudfilm i kapitel." },
      { label: "Företagsarvet", range: "180–250 kkr+", scope: "Bred dokumentation, huvudfilm och en serie korta klipp." },
    ],
    notes: {
      title: "Vad som styr priset",
      items: [
        "Antal personer som ska intervjuas och var de finns.",
        "Hur mycket arkivmaterial som behöver digitaliseras och bearbetas.",
        "Antal versioner och klipp som ska ut ur materialet.",
        "Om filmen ska visas på ett event med fast datum.",
        "Om engelsk version och textning behövs.",
      ],
    },
    faq: [
      {
        q: "Vad kostar en företagshistoriefilm?",
        a: "Typiskt 60 000–250 000 kr beroende på antal intervjuer, mängden arkivmaterial och hur många klipp som ska ut ur produktionen.",
      },
      {
        q: "Hur lång tid tar det?",
        a: "Räkna med 6–12 veckor från start till leverans. Inför ett jubileum planerar vi bakåt från datumet.",
      },
      {
        q: "Vi har nästan inget arkivmaterial. Går det ändå?",
        a: "Ja. Intervjuerna bär berättelsen. Arkivmaterial förstärker men är inte en förutsättning.",
      },
      {
        q: "Kan filmen användas i rekrytering?",
        a: "Ja, och det är ett av de vanligaste användningsområdena. Vi klipper då kortare versioner riktade mot kandidater ur samma material.",
      },
      {
        q: "Kan ni intervjua tidigare medarbetare och kunder?",
        a: "Ja. Externa röster ger perspektiv som interna intervjuer inte når, särskilt vid jubileum.",
      },
      {
        q: "Får vi råmaterialet?",
        a: "Ja, det kan ingå i leveransen så att ni kan använda materialet vidare internt.",
      },
    ],
    serviceName: "Företagshistoria på film",
    serviceType: "Företagshistoria på film",
    breadcrumbLabel: "Företagshistoria",
    relatedPricePath: "/pris/kunskapsoverforing",
    relatedPriceLabel: "Se prisguide för kunskapsöverföring",
  },
  {
    slug: "tyst-kunskap-dokumentation",
    audience: "foretag",
    eyebrow: "Heritage · För företag",
    h1: "Dokumentera tyst kunskap",
    metaTitle: "Dokumentera tyst kunskap på film — metod och pris | Fenny",
    metaDescription:
      "Tyst kunskap går inte att skriva ner i en handbok. Så fångar ni den på film: frågedesign, kapitelstruktur och användning i onboarding.",
    lede:
      "Tyst kunskap är det en erfaren medarbetare gör rätt utan att kunna förklara varför. Den fastnar sällan i en handbok — men den fastnar i en intervju.",
    question: "Hur dokumenterar man tyst kunskap?",
    quickAnswer:
      "Tyst kunskap dokumenteras genom filmade intervjuer där frågorna kräver konkreta situationer och undantag i stället för principer — och materialet kapitlas per kunskapsområde så det kan användas i onboarding när frågan faktiskt uppstår.",
    intro: [
      "Begreppet tyst kunskap beskriver det som en erfaren person vet men inte kan formulera i förväg: mönsterigenkänning, bedömningar, en känsla för när något är fel. Just därför misslyckas skriftlig dokumentation — den frågar efter en sammanfattning av något som bara existerar i situationer.",
      "Filmad intervju fungerar bättre av två skäl. Frågan kan riktas mot en konkret händelse, och svaret bär tonfall, tvekan och exempel som text tappar. Kvaliteten avgörs av frågedesignen, inte av kameran.",
      "Vår metod är att kartlägga kunskapsområden först, bygga frågor kring situationer och undantag, och klippa materialet per område så att det används i vardagen i stället för att arkiveras.",
    ],
    useCasesTitle: "Områden där det gör störst skillnad",
    useCases: [
      "Underhåll och felsökning i produktion",
      "Bedömning och offertsättning i projektverksamhet",
      "Kundrelationer med lång historik",
      "Kvalitetsbedömning i hantverk och tillverkning",
      "Säkerhetskritiska rutiner och undantagshantering",
      "Kulturbärande arbetssätt som ingen skrivit ner",
    ],
    process: [
      { label: "Kunskapskartläggning", body: "Vilka områden är kritiska och vem bär dem?" },
      { label: "Frågedesign", body: "Frågor byggda kring situationer, undantag och tidigare fel." },
      { label: "Intervju", body: "Filmning i arbetsmiljö när det hjälper svaren — vid maskinen, på plats." },
      { label: "Kapitelklippning", body: "Material per kunskapsområde, sökbart och användbart i utbildning." },
      { label: "Implementering", body: "Vi föreslår hur klippen läggs in i onboarding och intern utbildning." },
    ],
    tiers: [
      { label: "Nyckelperson-intervjun", range: "60–95 kkr", scope: "En person, kartläggning, frågedesign, intervju och leverans." },
      { label: "Kunskapsöverföring", range: "100–180 kkr", scope: "Intervjuserie, kapitlat material för onboarding och utbildning." },
      { label: "Företagsarvet", range: "180–250 kkr+", scope: "Flera personer och funktioner, sammanfattning plus internklipp." },
    ],
    notes: {
      title: "Frågedesign som fungerar",
      items: [
        "\"Berätta om en gång när det gick fel\" ger mer än \"hur gör du?\".",
        "Be om undantagen — det är där erfarenheten sitter.",
        "Fråga vad en ny medarbetare brukar missa.",
        "Filma i arbetsmiljön när kunskapen är knuten till platsen.",
        "Låt en oerfaren kollega ställa några av frågorna.",
      ],
    },
    faq: [
      {
        q: "Vad är tyst kunskap?",
        a: "Kunskap som bärs i erfarenhet och bedömning snarare än i dokument — det en van medarbetare gör rätt utan att kunna beskriva metoden i förväg.",
      },
      {
        q: "Varför fungerar inte skriftlig dokumentation?",
        a: "För att den ber om en sammanfattning av något som bara framträder i konkreta situationer. Intervjun kan gå till situationen; handboken kan inte det.",
      },
      {
        q: "Vad kostar det?",
        a: "Typiskt 60 000–250 000 kr beroende på antal personer och kunskapsområden. Många börjar med en person och en kritisk roll.",
      },
      {
        q: "Hur långt material blir det?",
        a: "Vanligen 2–4 timmar råmaterial per person som klipps till kapitel om 5–15 minuter per kunskapsområde.",
      },
      {
        q: "Kan ni filma i produktionsmiljö?",
        a: "Ja. Vi anpassar oss till skyddsrutiner och kan filma vid maskin, i verkstad eller på anläggning när kunskapen är platsbunden.",
      },
      {
        q: "Hur vet vi vilka områden vi ska prioritera?",
        a: "Vi börjar med en kartläggning: vilka roller är enpersonsberoenden, och vad skulle ta längst tid att bygga upp igen om personen försvann i morgon?",
      },
    ],
    serviceName: "Dokumentation av tyst kunskap",
    serviceType: "Kunskapsöverföring på film",
    breadcrumbLabel: "Tyst kunskap",
    relatedPricePath: "/pris/kunskapsoverforing",
    relatedPriceLabel: "Se prisguide för kunskapsöverföring",
  },
  {
    slug: "successionsplanering-dokumentation",
    audience: "foretag",
    eyebrow: "Heritage · För företag",
    h1: "Successionsplanering — dokumentera kunskapen bakom planen",
    metaTitle: "Successionsplanering — dokumentera kunskapen på film | Fenny",
    metaDescription:
      "Successionsplanen reglerar ägande och roller. Vi dokumenterar kunskapen bakom den på film: beslutslogik, relationer och erfarenhet. Från 60 000 kr.",
    lede:
      "En successionsplan beskriver vem som tar över vad och när. Den beskriver sällan vad personen faktiskt kan — och det är den delen som är svårast att ersätta.",
    question: "Vad är successionsplanering och vad ingår i den?",
    quickAnswer:
      "Successionsplanering är arbetet med att förbereda överlämningen av ägande, roller och ansvar i ett bolag. Planen omfattar normalt ägarstruktur, tidplan, tillträdare och finansiering — men den bör också omfatta hur kunskapen hos avträdande nyckelpersoner dokumenteras, eftersom den delen inte följer med i avtalen.",
    intro: [
      "Successionsplanering hanteras oftast av jurist, revisor och rådgivare: ägarstruktur, intern aktieöverlåtelse, värdering, finansiering och tidplan. Det arbetet är nödvändigt — men det överför inte kunskap.",
      "Det som saknas i planen är sällan formaliserat. Det är varför en viss kundrelation ser ut som den gör, vilka leverantörsbeslut som prövats, hur prissättningen egentligen sätts, och vilka avvägningar den avträdande gör utan att tänka på dem.",
      "Vi lägger till ett kunskapsspår i successionsplanen: strukturerade filmade intervjuer med avträdande ägare och nyckelpersoner, kapitlade per område så att tillträdaren kan gå tillbaka till rätt avsnitt när frågan faktiskt dyker upp — ofta ett år efter överlämningen.",
    ],
    useCasesTitle: "När kunskapsspåret behövs",
    useCases: [
      "Ägarledda bolag där planen är beslutad men överlämningen inte påbörjad",
      "Intern aktieöverlåtelse till nyckelpersoner eller ledning",
      "Successionsplan där tillträdaren kommer utifrån",
      "Bolag med få personer som bär kundrelationerna",
      "Styrelse som vill minska nyckelpersonsrisken före en försäljning",
      "Successionsplan som sträcker sig över flera år",
    ],
    process: [
      { label: "Genomgång av planen", body: "Vi utgår från er befintliga successionsplan och identifierar var kunskapsrisken sitter." },
      { label: "Kartläggning", body: "Vilka personer, roller och kunskapsområden riskerar att lämna bolaget med individen?" },
      { label: "Frågedesign", body: "Frågor som tvingar fram konkreta beslut och situationer, inte principer." },
      { label: "Intervjuserie", body: "Ett eller flera tillfällen, gärna med tillträdaren i rummet." },
      { label: "Kapitelklippning och leverans", body: "Materialet kapitlas per område och levereras för intern lagring. Sekretess enligt avtal." },
    ],
    tiers: [
      { label: "Nyckelperson-intervjun", range: "60–95 kkr", scope: "Avträdande ägare eller en nyckelperson, frågedesign, intervju och intern leverans." },
      { label: "Kunskapsöverföring", range: "100–180 kkr", scope: "Intervjuserie över flera tillfällen, kapitlat material för onboarding." },
      { label: "Företagsarvet", range: "180–250 kkr+", scope: "Flera nyckelpersoner, sammanfattande film och korta internklipp." },
      { label: "Löpande under successionen", range: "offert", scope: "Återkommande intervjuer planerade över planens tidshorisont." },
    ],
    notes: {
      title: "Så hänger det ihop med resten av planen",
      items: [
        "Kunskapsdokumentationen ersätter inte juridisk eller finansiell rådgivning — den kompletterar den.",
        "Bäst effekt när den startar 6–12 månader före tillträdet, medan båda parter finns kvar.",
        "Sekretessavtal tecknas innan första intervjun och ni godkänner frågeunderlaget.",
        "Materialet kan levereras för lagring helt i er egen miljö.",
        "Börja med en person och en kritisk roll innan ni bygger ut till fler.",
      ],
    },
    faq: [
      {
        q: "Vad är successionsplanering?",
        a: "Successionsplanering är det förberedande arbetet inför att ägande, roller och ansvar lämnas över i ett bolag. Planen omfattar ägarstruktur, tidplan, tillträdare och finansiering — och bör omfatta hur kunskapen hos de avträdande dokumenteras.",
      },
      {
        q: "Vad är en intern aktieöverlåtelse?",
        a: "En intern aktieöverlåtelse innebär att aktier överlåts till någon som redan finns i bolaget eller ägarkretsen — ofta nästa generation, ledningen eller nyckelpersoner. Själva överlåtelsen hanteras juridiskt och skattemässigt av era rådgivare. Vår del är kunskapen som inte följer med aktierna.",
      },
      {
        q: "När i successionsplaneringen bör dokumentationen göras?",
        a: "6–12 månader före tillträdet. Då finns tid för flera intervjutillfällen och för att tillträdaren hinner formulera sina egna frågor.",
      },
      {
        q: "Vad kostar det?",
        a: "Typiskt 60 000–250 000 kr beroende på antal nyckelpersoner och hur kapitlat materialet ska vara. Offert efter ett första samtal.",
      },
      {
        q: "Hur skiljer det sig från dokumentation vid generationsskifte?",
        a: "Innehållet överlappar. Successionsplanering är det bredare planeringsarbetet — det kan gälla ledning eller externa tillträdare. Generationsskifte avser överlämning inom familjen eller till nästa generation ägare.",
      },
      {
        q: "Hur hanteras känslig information?",
        a: "Sekretessavtal tecknas före första intervjun, ni godkänner frågeunderlaget och materialet kan levereras för lagring i er egen miljö.",
      },
    ],
    serviceName: "Kunskapsdokumentation vid successionsplanering",
    serviceType: "Kunskapsöverföring på film",
    breadcrumbLabel: "Successionsplanering",
    relatedPricePath: "/pris/kunskapsoverforing",
    relatedPriceLabel: "Se prisguide för kunskapsöverföring",
  },
  {
    slug: "generationsskifte-lantbruk-skog",
    audience: "foretag",
    eyebrow: "Heritage · Lantbruk och skog",
    h1: "Generationsskifte på gård, lantbruk och skogsfastighet",
    metaTitle: "Generationsskifte i lantbruk och skog på film | Fenny",
    metaDescription:
      "Vid generationsskifte på gård eller skogsfastighet försvinner kunskap om marken, djuren och besluten. Vi dokumenterar den på film. Från 60 000 kr.",
    lede:
      "På en gård sitter kunskapen i marken, årstiderna och besluten — sällan i pärmar. Vid ett generationsskifte är det den kunskapen som är svårast att lämna över.",
    question: "Hur gör man ett generationsskifte på gård eller skogsfastighet?",
    quickAnswer:
      "Ett generationsskifte på gård eller skogsfastighet hanteras juridiskt och skattemässigt av rådgivare, men den praktiska kunskapen — markens egenskaper, dikning, skiftenas historia, djurhållning, maskiner och återkommande beslut — behöver dokumenteras separat, helst genom filmade intervjuer ute på fastigheten medan båda generationerna finns på plats.",
    intro: [
      "Generationsskiften i lantbruk och skogsbruk skiljer sig från andra bolagsöverlämningar. Kunskapen är knuten till en specifik plats: vilket skifte som blir blött efter regn, var dikningen gjordes om, vilka bestånd som gallrades när, hur en viss maskin beter sig och vad som prövats genom åren.",
      "Mycket av det har aldrig skrivits ner, och en stor del går inte att förklara vid ett köksbord. Det behöver visas på plats — därför lägger vi ofta intervjun ute på fastigheten: i skogen, i ladugården, vid maskinerna.",
      "Utöver den praktiska kunskapen bär de flesta gårdar en släkthistoria: hur marken kom i familjen, vilka beslut som avgjorde, vad som en gång byggdes upp. Vi dokumenterar båda spåren i samma inspelning — driftkunskapen för den som tar över, och släkthistorien för familjen.",
    ],
    useCasesTitle: "Typiska situationer",
    useCases: [
      "Gård som går till nästa generation inom familjen",
      "Skogsfastighet där skötselhistoriken bara finns i huvudet",
      "Syskon som tar över gemensamt och behöver samma underlag",
      "Föräldrar som trappar ner men bor kvar på gården",
      "Arrende eller utomstående som tar över driften",
      "Familjelantbruk som fyller jämnt eller byter inriktning",
    ],
    process: [
      { label: "Förmöte", body: "Vi går igenom fastigheten, driften och vad som behöver finnas kvar efter överlämningen." },
      { label: "Frågeunderlag", body: "Frågor per område: mark, skog, djur, maskiner, ekonomi och historia." },
      { label: "Inspelning på plats", body: "Intervju ute på fastigheten där kunskapen kan visas, inte bara beskrivas." },
      { label: "Klippning i kapitel", body: "Materialet kapitlas per skifte, byggnad eller ämne. Gamla foton kan vävas in." },
      { label: "Leverans", body: "Filmen levereras digitalt till familjen och den som tar över driften." },
    ],
    tiers: [
      { label: "Överlämningsintervjun", range: "60–95 kkr", scope: "En person, förmöte, en inspelningsdag på fastigheten och klippt film." },
      { label: "Gårdens kunskap", range: "100–180 kkr", scope: "Flera inspelningstillfällen över säsong, kapitlat per område." },
      { label: "Gårdsarvet", range: "180–250 kkr+", scope: "Både driftkunskap och släkthistoria, huvudfilm och kortare klipp." },
      { label: "Löpande dokumentation", range: "offert", scope: "Inspelning vid flera tidpunkter över året — vår, skörd, vinter." },
    ],
    notes: {
      title: "Bra att veta",
      items: [
        "Vi ger inte juridisk eller skattemässig rådgivning kring själva överlåtelsen — det gör er rådgivare.",
        "Inspelning ute på fastigheten ger betydligt mer användbart material än en intervju inomhus.",
        "Årstiden påverkar. Vissa saker går bara att visa vid rätt tid på året.",
        "Har ni gamla foton, kartor eller skogsbruksplaner: samla ihop dem, de kan vävas in.",
        "Materialet är familjens. Vi använder det inte i marknadsföring utan skriftligt godkännande.",
      ],
    },
    faq: [
      {
        q: "Hur gör man ett generationsskifte på en gård?",
        a: "Den juridiska och skattemässiga delen — överlåtelseform, värdering, finansiering och tidplan — hanteras av jurist och rådgivare. Vid sidan av det behöver den praktiska kunskapen om marken, skogen, djuren och maskinerna dokumenteras, eftersom den inte finns i några handlingar.",
      },
      {
        q: "Varför räcker inte en skogsbruksplan eller driftpärm?",
        a: "Planer beskriver vad som finns och vad som ska göras. De beskriver inte varför tidigare beslut togs, vilka försök som misslyckats eller hur en viss del av fastigheten faktiskt beter sig.",
      },
      {
        q: "Var spelar ni in?",
        a: "Ute på fastigheten. Skogen, åkrarna, ladugården och maskinhallen — där kunskapen kan visas. Vi kompletterar ofta med ett lugnare samtal inomhus.",
      },
      {
        q: "Vad kostar det?",
        a: "Typiskt 60 000–250 000 kr beroende på omfattning och antal inspelningstillfällen. Offert lämnas efter ett första samtal.",
      },
      {
        q: "Kan vi dokumentera både driften och släkthistorien?",
        a: "Ja, och det är vanligast. Vi spelar in båda spåren och klipper dem antingen som en film i kapitel eller som två separata filmer.",
      },
      {
        q: "Hur lång tid tar det?",
        a: "Normalt 4–8 veckor från första samtal till färdig film. Ska materialet spelas in över flera årstider löper arbetet i stället över säsongen.",
      },
    ],
    serviceName: "Dokumentation vid generationsskifte i lantbruk och skogsbruk",
    serviceType: "Kunskapsöverföring på film",
    breadcrumbLabel: "Generationsskifte lantbruk och skog",
    relatedPricePath: "/pris/kunskapsoverforing",
    relatedPriceLabel: "Se prisguide för kunskapsöverföring",
  },
  {
    slug: "kompetensinventering",
    audience: "foretag",
    eyebrow: "Heritage · För företag",
    h1: "Kompetensinventering — och vad ni gör med resultatet",
    metaTitle: "Kompetensinventering: så gör ni och vad ni gör med resultatet",
    metaDescription:
      "En kompetensinventering visar vilken kunskap som finns och var den är sårbar. Vi dokumenterar de kritiska rollerna på film. Från 60 000 kr.",
    lede:
      "En kompetensinventering visar var kunskapen sitter. Nästa fråga är svårare: vad gör ni med de roller där bara en person kan jobbet?",
    question: "Vad är en kompetensinventering?",
    quickAnswer:
      "En kompetensinventering är en systematisk kartläggning av vilken kompetens som finns i organisationen, var den sitter och var den är sårbar. Resultatet visar vilka roller som är enpersonsberoenden — och det är dem som behöver dokumenteras innan personen slutar, går i pension eller byter roll.",
    intro: [
      "De flesta kompetensinventeringar landar i en matris: roller, kompetensområden och nivåer. Den är användbar för rekrytering och utveckling, men den löser inte grundproblemet den ofta blottar — att viss kunskap bara finns hos en person och aldrig lämnat den personens huvud.",
      "Det är där vi kommer in. Vi utgår från er inventering, eller hjälper er göra en enkel riskbaserad variant, och tar de roller där bortfallet skulle kosta mest. Sedan dokumenterar vi kunskapen genom strukturerade filmade intervjuer.",
      "Skillnaden mot en skriftlig rutin är att filmen fångar bedömningen mellan stegen: hur personen läser en situation, vilka undantag som finns och vad som brukar gå fel. Det är den delen som tar längst tid att bygga upp igen.",
    ],
    useCasesTitle: "När det blir aktuellt",
    useCases: [
      "Pensionsavgångar inom 1–3 år i nyckelroller",
      "Enpersonsberoenden som inventeringen synliggjort",
      "Omorganisation där roller slås ihop eller flyttas",
      "Snabb tillväxt där kunskapen inte hinner spridas",
      "Onboarding som tar för lång tid i en specifik roll",
      "Certifieringar och krav på dokumenterad kompetens",
    ],
    process: [
      { label: "Utgångsläge", body: "Vi utgår från er kompetensinventering, eller gör en enkel riskbaserad kartläggning tillsammans." },
      { label: "Prioritering", body: "Vilka roller skulle ta längst tid att bygga upp igen om personen slutade i morgon?" },
      { label: "Frågedesign", body: "Frågor byggda på verkliga situationer och beslut, inte på generella beskrivningar." },
      { label: "Filmade intervjuer", body: "Intervju med de prioriterade personerna, gärna på arbetsplatsen där arbetet görs." },
      { label: "Kapitelklippning och leverans", body: "Materialet kapitlas per område för intranät, LMS eller intern lagring." },
    ],
    tiers: [
      { label: "En kritisk roll", range: "60–95 kkr", scope: "Prioritering, frågedesign, en inspelningsdag och intern leverans." },
      { label: "Kunskapsöverföring", range: "100–180 kkr", scope: "Flera roller eller flera tillfällen, kapitlat material för onboarding." },
      { label: "Programnivå", range: "180–250 kkr+", scope: "Flera avdelningar, sammanfattande film och korta internklipp." },
      { label: "Löpande dokumentation", range: "offert", scope: "Återkommande intervjuer planerade över året." },
    ],
    notes: {
      title: "Så håller ni arbetet hanterbart",
      items: [
        "Inventera inte allt. Prioritera efter risk: konsekvens om personen försvinner, gånger sannolikheten att det sker.",
        "Börja med en roll. Det ger ett konkret underlag att ta beslut på innan ni bygger ut.",
        "Filmat material ersätter inte rutinbeskrivningar — det fångar bedömningen som rutinen inte rymmer.",
        "Sekretessavtal tecknas före första intervjun och ni godkänner frågeunderlaget.",
        "Materialet kan levereras för lagring helt i er egen miljö.",
      ],
    },
    faq: [
      {
        q: "Vad är en kompetensinventering?",
        a: "En systematisk kartläggning av vilken kompetens som finns i organisationen, var den sitter och var den är sårbar. Den används för rekrytering, utveckling och riskbedömning — och synliggör ofta roller där bara en person kan jobbet.",
      },
      {
        q: "Hur gör man en kompetensinventering?",
        a: "Utgå från rollerna, inte från individerna. Lista vilka kunskapsområden varje roll kräver, vem som faktiskt behärskar dem och vad som skulle hända om personen slutade. Prioritera sedan de områden där både konsekvens och sannolikhet är höga.",
      },
      {
        q: "Gör ni själva inventeringen?",
        a: "Vi gör ingen HR-inventering i verktyg eller system. Vi kan hjälpa till med en enkel riskbaserad kartläggning som underlag, men vår tyngdpunkt ligger på att dokumentera kunskapen i de roller ni prioriterar.",
      },
      {
        q: "Varför dokumentera på film i stället för i text?",
        a: "Skriftliga rutiner fångar stegen. Filmade intervjuer fångar bedömningen mellan stegen — undantagen, avvägningarna och det som brukar gå fel. Det är den delen som praktiker sällan tänker på att skriva ner.",
      },
      {
        q: "Vad kostar det?",
        a: "Från 60 000 kr för en roll och en inspelningsdag. Bredare program ligger typiskt på 100 000–250 000 kr. Offert efter ett första samtal.",
      },
      {
        q: "Var börjar vi?",
        a: "Med den roll som skulle ta längst tid att bygga upp igen om personen slutade i morgon. Ett kort samtal räcker för att identifiera den.",
      },
    ],
    serviceName: "Kompetensinventering och kunskapsdokumentation",
    serviceType: "Kunskapsöverföring på film",
    breadcrumbLabel: "Kompetensinventering",
    relatedPricePath: "/pris/kunskapsoverforing",
    relatedPriceLabel: "Se prisguide för kunskapsöverföring",
  },
];


// ---------------------------------------------------------------------------
// Engelska speglingar (/en/heritage/<slug>). Endast de kluster där engelsk
// efterfrågan är rimlig: internationella familjer i Sverige och HR/ledning i
// bolag med engelska som koncernspråk. Samma prisdata, översatt copy.
// ---------------------------------------------------------------------------
const EN_CONTENT: Record<string, HeritageTopicEn> = {
  "livsberattelse-till-foralder": {
    eyebrow: "Heritage · For families",
    h1: "A filmed life story for a parent or grandparent",
    metaTitle: "Filmed life story for a parent | Fenny Heritage",
    metaDescription:
      "Give a parent or grandparent a professionally filmed life story. We plan the questions, record calmly at home and deliver the film privately. From SEK 35,000.",
    lede:
      "Most families want to preserve a parent's story but do not know how to start. We handle the questions, the recording and the edit — you only need to book the day.",
    question: "How do you document a parent's life story?",
    quickAnswer:
      "A parent's life story is best documented through a planned, filmed interview at home: question areas are prepared together with the family, the interview is recorded at an unhurried pace, and the material is edited into a film the family owns privately.",
    intro: [
      "The most common Fenny Heritage commission comes from children or grandchildren who want to preserve a parent's story before time runs out. The initiative almost never comes from the person themselves — and that is usually where the hesitation sits: how do you ask, what do you ask about, and how do you do it without making it uncomfortable?",
      "Our job is to remove that friction. We hold a pre-meeting with the family, define the question areas together, and let the person being interviewed see the questions in advance if that makes them more comfortable. During recording we keep the room small, the equipment discreet and the pace theirs.",
      "The result is a film that grandchildren and future generations can watch — with the voice, expressions, pauses and laughter preserved, not just the facts.",
    ],
    useCasesTitle: "Common reasons to do it now",
    useCases: [
      "A gift for a milestone birthday or Christmas",
      "Before memory or health changes",
      "Grandchildren who should hear the story in their own words",
      "After a death in the family — the surviving parent tells the story",
      "Family history only one person still carries",
      "Moving out of the family home before the place is gone",
    ],
    process: [
      { label: "Pre-meeting", body: "We go through who will be interviewed, the purpose and what you want preserved." },
      { label: "Question outline", body: "We define areas and questions together. The person can see them in advance." },
      { label: "Filmed interview", body: "Recorded at home or in a place of meaning. A small team, a calm pace." },
      { label: "Editing", body: "A chaptered main film, with the option to weave in photos and archive material." },
      { label: "Private delivery", body: "Delivered digitally to the family. You decide who gets to see it." },
    ],
    tiers: [
      { label: "The Life Interview", range: "From SEK 35,000", scope: "One interview, one person, main film and digital delivery." },
      { label: "The Family Legacy", range: "SEK 55,000–95,000", scope: "Longer interview, chapter structure, photos and archive material, shorter excerpts." },
      { label: "Legacy", range: "From SEK 120,000", scope: "Several interviews, narrative structure, archive research, main film and clips." },
    ],
    notes: {
      title: "What affects scope and price",
      items: [
        "Number of people interviewed and number of recording days",
        "Whether photos, letters or archive film should be included",
        "Length of the main film and how many shorter excerpts you want",
        "Travel outside our locations in Stockholm, Gothenburg, Malmö and Västerås",
        "Whether you want subtitles or an English-language version",
      ],
    },
    faq: [
      { q: "What if my parent does not want to be filmed?", a: "Start with the reason rather than the camera. Most say yes when they understand it is for the grandchildren, that they see the questions in advance and that nothing is published. We can also keep the setup very small." },
      { q: "How long does the interview take?", a: "Recording usually takes two to four hours including breaks, and we adapt the pace entirely to the person." },
      { q: "Can we do this in English?", a: "Yes. We work in both Swedish and English, and can subtitle the film in either language." },
      { q: "How quickly can you record?", a: "When health is a factor we prioritise the booking and can normally record within one to two weeks." },
      { q: "Who owns the film?", a: "You do. The film is delivered digitally to the family and is never published by us." },
    ],
    serviceName: "Filmed life story for a parent",
    serviceType: "Filmed life story",
    breadcrumbLabel: "Life story for a parent",
    relatedPriceLabel: "See the price guide for filmed life stories",
  },
  minnesfilm: {
    eyebrow: "Heritage · For families",
    h1: "Memorial film for a milestone, anniversary or memorial service",
    metaTitle: "Memorial film — price and process | Fenny Heritage",
    metaDescription:
      "A memorial film gathers a life, a relationship or a milestone into one film to watch together. Interviews, photos and archive material. From SEK 35,000.",
    lede:
      "A film to watch together — at a birthday, an anniversary or a memorial service. We combine interviews, photographs and archive material into one coherent story.",
    question: "What is a memorial film?",
    quickAnswer:
      "A memorial film is a short, edited film that gathers a person's life or a shared history into one narrative, usually built from filmed interviews combined with photographs and archive material, and made to be watched together at a milestone or memorial service.",
    intro: [
      "A memorial film differs from a full life story in ambition rather than care. The life story documents; the memorial film distils. It is made to be watched in one sitting, by a room full of people, at a specific occasion.",
      "In practice we work from what already exists — photographs, older film, letters — and add one or more filmed interviews to give the material a voice. The edit gives it a beginning, a turn and an ending.",
      "Because the film is tied to a date, planning matters. Contact us in good time so recording, archive work and editing fit before the occasion.",
    ],
    useCasesTitle: "When a memorial film is the right format",
    useCases: [
      "A milestone birthday where the family gathers",
      "A wedding anniversary or a shared life story",
      "A memorial service or the anniversary of a death",
      "A retirement after a long career",
      "A company or association jubilee",
      "A tribute to someone who cannot tell the story themselves",
    ],
    process: [
      { label: "First conversation", body: "We set the occasion, the audience, the tone and the deadline." },
      { label: "Material review", body: "We go through photographs, archive film and other material together." },
      { label: "Interviews", body: "One or more filmed interviews with family, friends or colleagues." },
      { label: "Editing", body: "Narrative edit with music, sound and colour work, adapted to the occasion." },
      { label: "Delivery", body: "Delivered in a format ready to screen at the venue, plus a private digital copy." },
    ],
    tiers: [
      { label: "Short memorial film", range: "From SEK 35,000", scope: "One interview or existing material, film of roughly 5–10 minutes." },
      { label: "Extended memorial film", range: "SEK 55,000–95,000", scope: "Several interviews, archive work, music and a longer narrative." },
      { label: "Memorial film with legacy", range: "From SEK 120,000", scope: "Memorial film plus a full documented life story for the family." },
    ],
    notes: {
      title: "What affects scope and price",
      items: [
        "How much archive material needs to be digitised and restored",
        "Number of people interviewed and locations",
        "Deadline — a short lead time may require priority scheduling",
        "Music licensing for public screening",
        "Whether you also want a longer documented life story",
      ],
    },
    faq: [
      { q: "How far in advance should we book?", a: "Four to eight weeks is comfortable. Shorter lead times are sometimes possible but limit interviews and archive work." },
      { q: "Can you use old photographs and video?", a: "Yes. We digitise, clean up and integrate photographs, film and documents into the edit." },
      { q: "Can the film be shown at a venue?", a: "Yes. We deliver a screening-ready file and can advise on format and sound at the venue." },
      { q: "What does a memorial film cost?", a: "From SEK 35,000 for a short film. Extended films with several interviews and archive work are SEK 55,000–95,000." },
      { q: "Can it be made if the person has died?", a: "Yes. We then build the film from archive material and interviews with those who knew them." },
    ],
    serviceName: "Memorial film",
    serviceType: "Memorial film",
    breadcrumbLabel: "Memorial film",
    relatedPriceLabel: "See the price guide for memorial films",
  },
  "pensionsavgang-kunskapsoverforing": {
    eyebrow: "Heritage · For organisations",
    h1: "Knowledge transfer before a key person retires",
    metaTitle: "Knowledge transfer before retirement | Fenny",
    metaDescription:
      "Capture what a retiring key person knows before the last day. Structured filmed interviews for onboarding and internal training. From SEK 60,000.",
    lede:
      "When a senior specialist retires, the reasoning leaves with them. Structured filmed interviews capture the judgement that written documentation never holds.",
    question: "How do you transfer knowledge before an employee retires?",
    quickAnswer:
      "Knowledge transfer before retirement works best as a series of structured filmed interviews started six to twelve months before the last working day, where an external interviewer asks the naive questions that force the implicit reasoning into words, and the material is chaptered for onboarding.",
    intro: [
      "Handover periods are usually planned around tasks and systems. What rarely gets captured is judgement: how the person reads a situation, which risks they weigh, and why they reject the obvious answer.",
      "That knowledge does not surface in a checklist. It surfaces in conversation — particularly when the person asking does not already share the assumptions.",
      "Starting early matters. In the final weeks the calendar is full and the person is already mentally elsewhere. Six to twelve months out, there is room for reflection.",
    ],
    useCasesTitle: "Typical situations",
    useCases: [
      "A specialist retiring within the year",
      "A single point of failure in a critical role",
      "A founder or long-serving manager stepping back",
      "Technical judgement in production or maintenance",
      "Customer relationships built over decades",
      "Roles where written routines exist but explain nothing",
    ],
    process: [
      { label: "Mapping", body: "We identify the roles and areas where experience, not routine, carries the value." },
      { label: "Interview design", body: "We build a question structure with the manager and the person leaving." },
      { label: "Filmed interviews", body: "One or several sessions, recorded on site in a working environment." },
      { label: "Editing and chaptering", body: "Material is cut into searchable chapters by theme and situation." },
      { label: "Internal delivery", body: "Delivered for your intranet, LMS or onboarding programme." },
    ],
    tiers: [
      { label: "Key Person Interview", range: "From SEK 60,000", scope: "One person, one recording day, chaptered material." },
      { label: "Knowledge Transfer", range: "SEK 90,000–180,000", scope: "Interview series, several sessions, structured chapters for onboarding." },
      { label: "Company Legacy", range: "From SEK 200,000", scope: "Several people, company history, culture and knowledge in one programme." },
    ],
    notes: {
      title: "What affects scope and price",
      items: [
        "Number of people and recording days",
        "How much of the material needs chaptering and internal packaging",
        "Whether the material is for internal use only or also external communication",
        "Locations and travel",
        "Language — Swedish, English or subtitled in both",
      ],
    },
    faq: [
      { q: "How early should we start?", a: "Six to twelve months before the last working day. Later is still worthwhile, but the final weeks leave little room for reflection." },
      { q: "Why not document it internally?", a: "Colleagues share the person's assumptions and skip the very things a newcomer needs. An external interviewer asks the naive questions." },
      { q: "What does it cost?", a: "From SEK 60,000 for one person and one recording day. Interview series are SEK 90,000–180,000 depending on scope." },
      { q: "Is the material used in practice?", a: "It is when it is chaptered by situation rather than delivered as a two-hour recording. That is why editing is part of the work." },
      { q: "Can the material stay internal?", a: "Yes. Company material is delivered for internal use and is never published by us." },
    ],
    serviceName: "Knowledge transfer before retirement",
    serviceType: "Filmed knowledge transfer",
    breadcrumbLabel: "Knowledge transfer before retirement",
    relatedPriceLabel: "See the price guide for knowledge transfer",
  },
  "tyst-kunskap-dokumentation": {
    eyebrow: "Heritage · For organisations",
    h1: "Documenting tacit knowledge on film",
    metaTitle: "Document tacit knowledge on film | Fenny Heritage",
    metaDescription:
      "Tacit knowledge resists written documentation. Filmed interviews capture reasoning, judgement and pitfalls in a form new colleagues can actually learn from.",
    lede:
      "The knowledge that matters most is the hardest to write down. Film captures the reasoning behind the decision, not just the decision.",
    question: "How do you document tacit knowledge?",
    quickAnswer:
      "Tacit knowledge is documented by having an external interviewer ask a practitioner to reason through concrete cases on film — why a decision was made, what was weighed and what usually goes wrong — because the reasoning surfaces in conversation about specific situations, not in written routines.",
    intro: [
      "Tacit knowledge is what a practitioner knows without being able to state it: how a machine sounds when it is about to fail, when a customer is about to walk, which shortcut is safe and which is not.",
      "Written documentation captures steps. It rarely captures the judgement between the steps, because the person writing does not see it as remarkable.",
      "The method that works is concrete: talk about real cases, ask why at every turn, and let the person think out loud. Film preserves that thinking in a form colleagues can learn from.",
    ],
    useCasesTitle: "Where tacit knowledge tends to sit",
    useCases: [
      "Production and maintenance — reading machines and materials",
      "Sales — judging a deal and a relationship",
      "Engineering — knowing which trade-offs are acceptable",
      "Care and service — handling situations no routine covers",
      "Leadership — culture, tone and unwritten rules",
      "Craft trades — technique that is shown rather than described",
    ],
    process: [
      { label: "Case selection", body: "We identify concrete situations where judgement, not routine, decided the outcome." },
      { label: "Interview design", body: "Questions are built around cases so reasoning is prompted rather than summarised." },
      { label: "Filmed interviews", body: "Recorded in the working environment, often with the work itself in frame." },
      { label: "Editing and chaptering", body: "Cut into short, searchable sequences per situation." },
      { label: "Internal delivery", body: "Packaged for onboarding, internal training or the intranet." },
    ],
    tiers: [
      { label: "One role", range: "From SEK 60,000", scope: "One practitioner, one recording day, chaptered material." },
      { label: "Several roles", range: "SEK 90,000–180,000", scope: "Interview series across roles or shifts, structured chapters." },
      { label: "Programme", range: "From SEK 200,000", scope: "Organisation-wide documentation with a reusable structure." },
    ],
    notes: {
      title: "What affects scope and price",
      items: [
        "Number of roles and recording days",
        "Whether the work itself needs to be filmed alongside the interview",
        "Chaptering and packaging for your onboarding system",
        "Confidentiality requirements and locations",
        "Language — Swedish, English or subtitled in both",
      ],
    },
    faq: [
      { q: "Why is written documentation not enough?", a: "Written routines capture steps. Tacit knowledge is the judgement between the steps, which the practitioner rarely sees as worth writing down." },
      { q: "How do you get someone to explain what they do instinctively?", a: "By talking about concrete cases rather than general process, and by asking why repeatedly. An outsider's naive questions do most of the work." },
      { q: "How long does it take?", a: "Four to eight weeks from first meeting to delivered material for a single role." },
      { q: "What does it cost?", a: "From SEK 60,000 for one role and one recording day. Broader programmes are SEK 90,000–180,000." },
      { q: "Where do we start?", a: "With the role that would take longest to rebuild if the person left tomorrow." },
    ],
    serviceName: "Documentation of tacit knowledge",
    serviceType: "Filmed knowledge transfer",
    breadcrumbLabel: "Tacit knowledge",
    relatedPriceLabel: "See the price guide for knowledge transfer",
  },
};

for (const topic of HERITAGE_TOPICS) {
  const en = EN_CONTENT[topic.slug];
  if (en) topic.en = en;
}

export function getHeritageTopic(slug: string): HeritageTopic | undefined {
  return HERITAGE_TOPICS.find((t) => t.slug === slug);
}

export const HERITAGE_TOPIC_PATHS = HERITAGE_TOPICS.map((t) => `/heritage/${t.slug}`);

/** Topics som har en engelsk spegling under /en/heritage/<slug>. */
export const HERITAGE_TOPIC_EN_SLUGS = Object.keys(EN_CONTENT);
